import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { CompanyParameterReader } from '../company-parameters/company-parameter.reader';

/**
 * Zona horaria del cliente.
 *
 * Las fechas con hora se muestran en la zona del cliente, no en la del navegador.
 * Quien la define es el parametro de empresa `COMPANY_TIME_ZONE` de Simappe
 * Admin, un identificador IANA (p. ej. `America/Bogota`), por el mismo camino
 * que el perfil visual. El kit la pide en cada fecha que escribe, asi que se
 * responde desde el almacenamiento local, sin red: al autenticarse se consulta
 * la vigente y queda guardada.
 */
@Injectable({ providedIn: 'root' })
export class TimeZoneService {
  private readonly auth = inject(AuthService);
  private readonly parametros = inject(CompanyParameterReader);

  /** Clave del almacenamiento local donde viaja la zona entre sesiones. */
  private static readonly STORAGE_KEY = 'time_zone';

  /** Nombre del parametro en el maestro de Simappe Admin. */
  static readonly PARAMETER_NAME = 'COMPANY_TIME_ZONE';

  /**
   * Deja pedida la zona vigente cada vez que hay sesion. Corre una sola vez, en
   * el arranque.
   */
  start(): void {
    this.auth.isAuthenticated$
      .pipe(
        filter((autenticado) => autenticado),
        switchMap(() => this.refresh()),
      )
      .subscribe();
  }

  /**
   * Consulta la zona vigente de la compania y la guarda. Si no se pudo saber,
   * queda la que habia.
   *
   * @returns cuando termina.
   */
  refresh(): Observable<void> {
    return this.parametros.valor(TimeZoneService.PARAMETER_NAME).pipe(
      tap((zona) => {
        if (zona) this.store(zona);
      }),
      map(() => void 0),
    );
  }

  /**
   * Zona horaria del cliente, o nulo si aun no se sabe; con nulo, el kit usa la
   * del navegador.
   *
   * @returns el identificador IANA.
   */
  zona(): string | null {
    try {
      return localStorage.getItem(TimeZoneService.STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private store(zona: string): void {
    try {
      localStorage.setItem(TimeZoneService.STORAGE_KEY, zona);
    } catch {
      // Sin almacenamiento la zona no persiste y el kit usa la del navegador.
    }
  }
}
